import { colors } from '@hqo/react-components-library/dist/hqo-theme-provider';
import { Check, Clock, LogOut, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { VisitStatus } from 'store/visits/types';

/**
 * The colour each status is drawn in, shared by the list badge and the detail heading.
 *
 * One map rather than one per surface: a status that reads grey in the list and green on the detail is
 * how a cancelled visit ends up wearing a confirmation tick.
 */
export const STATUS_COLORS: ReadonlyMap<VisitStatus, string> = new Map([
  [VisitStatus.INVITED, colors.palette.systemPurple],
  [VisitStatus.CHECKED_IN, colors.palette.systemGreenAlt],
  [VisitStatus.CHECKED_OUT, colors.palette.systemOrange],
  [VisitStatus.CANCELED, colors.palette.systemGreyDarkAlt],
  [VisitStatus.AWAITING_APPROVAL, colors.palette.systemOrange],
  [VisitStatus.VISIT_APPROVED, colors.palette.systemGreenAlt],
  [VisitStatus.VISIT_DENIED, colors.palette.systemPink],
  [VisitStatus.PREREGISTERED, colors.palette.systemPurple],
  [VisitStatus.NO_SHOW, colors.palette.systemGreyDarkAlt],
]);

export const DEFAULT_STATUS_COLOR = colors.palette.systemGreyDarkAlt;

/** The mark shown against each status: settled, refused, waiting, or done. */
const STATUS_GLYPHS: ReadonlyMap<VisitStatus, LucideIcon> = new Map([
  [VisitStatus.INVITED, Clock],
  [VisitStatus.PREREGISTERED, Clock],
  [VisitStatus.AWAITING_APPROVAL, Clock],
  [VisitStatus.VISIT_APPROVED, Check],
  [VisitStatus.CHECKED_IN, Check],
  [VisitStatus.CHECKED_OUT, LogOut],
  [VisitStatus.CANCELED, X],
  [VisitStatus.VISIT_DENIED, X],
  [VisitStatus.NO_SHOW, X],
]);

/**
 * The heading each status is announced with — a sentence, not the bare status word.
 *
 * Full sentences rather than a "Visit {status}" template: French renders these as "Visite annulée",
 * where the adjective agrees with the noun, so a template built from the list-badge word would produce
 * "Visite Annulé". `canceled`, `pending` and `confirmed` already exist and are translated in all 16
 * locales, so those are reused rather than duplicated.
 */
const STATUS_HEADING_IDS: ReadonlyMap<VisitStatus, string> = new Map([
  [VisitStatus.INVITED, 'labels.visit.status.expected'],
  [VisitStatus.PREREGISTERED, 'labels.visit.status.preregistered'],
  [VisitStatus.AWAITING_APPROVAL, 'labels.visit.status.pending'],
  [VisitStatus.VISIT_APPROVED, 'labels.visit.status.approved'],
  [VisitStatus.CHECKED_IN, 'labels.visit.status.checkedIn'],
  [VisitStatus.CHECKED_OUT, 'labels.visit.status.checkedOut'],
  [VisitStatus.CANCELED, 'labels.visit.status.canceled'],
  [VisitStatus.VISIT_DENIED, 'labels.visit.status.denied'],
  [VisitStatus.NO_SHOW, 'labels.visit.status.missed'],
]);

/** The confirmation heading, used when no status is being described. */
export const CONFIRMED_HEADING_ID = 'labels.visit.status.confirmed';

export const getStatusHeadingId = (status?: VisitStatus): string =>
  (status && STATUS_HEADING_IDS.get(status)) || CONFIRMED_HEADING_ID;

export const getStatusColor = (status?: VisitStatus): string =>
  (status && STATUS_COLORS.get(status)) || DEFAULT_STATUS_COLOR;

export const getStatusGlyph = (status?: VisitStatus): LucideIcon => (status && STATUS_GLYPHS.get(status)) || Check;
